import React, { useState, useEffect } from 'react';
import {
  Loader,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileText,
  Calendar,
  Plus,
  Trash2,
  GripVertical,
  Save,
  X,
  Eye,
  Settings
} from 'lucide-react';
import { formatCurrency, formatDate, subscriptionAPI, locTemplatesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'file', label: 'File Upload' }
];

const CreateLOC = () => {
  const { user } = useAuth();
  const [myTemplates, setMyTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBond, setSelectedBond] = useState(null);
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showBondSelectionModal, setShowBondSelectionModal] = useState(false);
  const [availableBonds, setAvailableBonds] = useState([]);
  const [loadingAvailableBonds, setLoadingAvailableBonds] = useState(false);
  const [previewingTemplateId, setPreviewingTemplateId] = useState(null);
  
  // Template state
  const [templateName, setTemplateName] = useState('');
  const [fields, setFields] = useState([]);
  const [templateConfig, setTemplateConfig] = useState({
    title: 'Letter of Commitment',
    headerText: '',
    footerText: '',
    sections: [],
    fontFamily: 'Helvetica',
    fontSize: 12,
    primaryColor: '#000000',
    pageSize: 'A4',
    orientation: 'portrait',
    margins: { top: 50, right: 50, bottom: 50, left: 50 }
  });
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null);
  const [previewingTemplate, setPreviewingTemplate] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  
  // PDF Template state
  const [pdfTemplateFileName, setPdfTemplateFileName] = useState('Sample-PDF-Fillable.pdf');
  const [pdfFormFields, setPdfFormFields] = useState([]);
  const [loadingPdfFields, setLoadingPdfFields] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  // Fetch templates created by this PS
  const fetchMyTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await locTemplatesAPI.getMyTemplates();

      if (response.data.success) {
        // API response structure: { success: true, data: { templates: [...] } }
        setMyTemplates(response.data.data?.templates || response.data.templates || []);
      } else {
        throw new Error(response.data.message || 'Failed to fetch templates');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError(error.message);
      setMyTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available bonds for selection (bonds with subscription periods)
  const fetchAvailableBonds = async () => {
    try {
      setLoadingAvailableBonds(true);

      const response = await subscriptionAPI.getSubscriptionBonds({ limit: 100 });

      if (response.data.success) {
        const now = new Date();
        const bondsReadyForLOC = (response.data.data || []).filter(bond => {
          // Filter out listed bonds - LOC is only used before issuance day
          const isListedBond = bond.status === 'listed';
          if (isListedBond) return false;

          const hasSubscriptionPeriod = bond.subscriptionStartDate && bond.subscriptionEndDate;
          if (!hasSubscriptionPeriod) return false;

          const startDate = new Date(bond.subscriptionStartDate);
          const endDate = new Date(bond.subscriptionEndDate);

          return now <= endDate;
        }).map(bond => ({
          ...bond,
          subscriptions: bond.offChainBiddersEntered || 0,
          totalSubscribed: bond.totalSubscribed || bond.totalNominalValue || 0,
          subscriptionRatio: bond.subscriptionRatio || 1.0,
          nominalAmount: bond.totalNominalValue || 0,
          productType: bond.bondType === 'Sukuk' ? 'Sukuk' :
            bond.bondType === 'Conventional Bonds' ? 'Conventional Bonds' :
            (bond.bondType === 'Islamic' ? 'Sukuk' : 'Conventional Bonds'),
          status: bond.status || 'Ready for Allocation',
          subscriptionStartDate: bond.subscriptionStartDate,
          subscriptionEndDate: bond.subscriptionEndDate,
          issuer: bond.issuer || 'Unknown Issuer',
          bondName: bond.bondName,
          isin: bond.isin || '',
          stockCode: bond.stockCode || '',
          profitRate: bond.profitRate || bond.couponRate,
          couponRate: bond.couponRate
        }));

        // Filter out bonds that already have ACTIVE templates
        const activeTemplateBondIds = new Set(
          myTemplates
            .filter(t => t.status === 'active')
            .map(t => String(t.bondId?._id || t.bondId?.id || t.bondId))
        );
        const availableBondsFiltered = bondsReadyForLOC.filter(bond => 
          !activeTemplateBondIds.has(String(bond.id || bond._id))
        );

        setAvailableBonds(availableBondsFiltered);
      } else {
        setAvailableBonds([]);
      }
    } catch (error) {
      console.error('Error fetching available bonds:', error);
      setAvailableBonds([]);
    } finally {
      setLoadingAvailableBonds(false);
    }
  };

  useEffect(() => {
    fetchMyTemplates();
  }, []);

  useEffect(() => {
    if (showBondSelectionModal) {
      fetchAvailableBonds();
    }
  }, [showBondSelectionModal, myTemplates]);

  const handleCreateLOC = (bond) => {
    setSelectedBond(bond);
    setTemplateName(`${bond.bondName} - LOC Template`);
    setFields([]);
    setTemplateConfig({
      title: 'Letter of Commitment',
      headerText: '',
      footerText: '',
      sections: [],
      fontFamily: 'Helvetica',
      fontSize: 12,
      primaryColor: '#000000',
      pageSize: 'A4',
      orientation: 'portrait',
      margins: { top: 50, right: 50, bottom: 50, left: 50 }
    });
    setShowTemplateBuilder(true);
    setShowBondSelectionModal(false);
  };

  const handlePreviewExistingTemplate = async (template) => {
    if (!template || previewingTemplateId) return;

    try {
      setPreviewingTemplateId(template._id || template.id);
      
      // Generate PDF with empty/default field data
      const fieldData = {};
      template.fields?.forEach(field => {
        if (field.defaultValue !== undefined) {
          fieldData[field.name] = field.defaultValue;
        }
      });

      const response = await locTemplatesAPI.generatePDF(template._id || template.id, fieldData);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      
      // Open in new window
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Error previewing template:', error);
      alert('Failed to preview template. Please try again.');
    } finally {
      setPreviewingTemplateId(null);
    }
  };

  // Fetch PDF form fields when template builder opens
  useEffect(() => {
    const fetchPDFFormFields = async () => {
      if (showTemplateBuilder && pdfTemplateFileName) {
        try {
          setLoadingPdfFields(true);
          const response = await locTemplatesAPI.getPDFFormFields(pdfTemplateFileName);
          if (response.data.success) {
            setPdfFormFields(response.data.data.fields || []);
          }
        } catch (error) {
          console.error('Error fetching PDF form fields:', error);
          setPdfFormFields([]);
        } finally {
          setLoadingPdfFields(false);
        }
      }
    };

    fetchPDFFormFields();
  }, [showTemplateBuilder, pdfTemplateFileName]);

  // Load PDF template preview
  const handleViewPDFTemplate = async () => {
    try {
      const response = await locTemplatesAPI.getPDFTemplate(pdfTemplateFileName);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfPreviewUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error loading PDF template:', error);
      alert('Failed to load PDF template. Please try again.');
    }
  };

  const handleAddField = () => {
    const newField = {
      name: `field_${Date.now()}`,
      type: 'text',
      label: 'New Field',
      placeholder: '',
      required: false,
      pdfFieldName: '', // PDF form field name to map to
      order: fields.length,
      validation: {},
      options: [],
      defaultValue: ''
    };
    setFields([...fields, newField]);
  };

  const handleRemoveField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    // Reorder fields
    newFields.forEach((field, i) => {
      field.order = i;
    });
    setFields(newFields);
  };

  const handleFieldChange = (index, key, value) => {
    const newFields = [...fields];
    newFields[index][key] = value;
    setFields(newFields);
  };

  const handleFieldValidationChange = (index, key, value) => {
    const newFields = [...fields];
    if (!newFields[index].validation) {
      newFields[index].validation = {};
    }
    newFields[index].validation[key] = value;
    setFields(newFields);
  };

  const handleAddDropdownOption = (fieldIndex) => {
    const newFields = [...fields];
    if (!newFields[fieldIndex].options) {
      newFields[fieldIndex].options = [];
    }
    newFields[fieldIndex].options.push({ label: '', value: '' });
    setFields(newFields);
  };

  const handleDropdownOptionChange = (fieldIndex, optionIndex, key, value) => {
    const newFields = [...fields];
    newFields[fieldIndex].options[optionIndex][key] = value;
    setFields(newFields);
  };

  const handleRemoveDropdownOption = (fieldIndex, optionIndex) => {
    const newFields = [...fields];
    newFields[fieldIndex].options = newFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    setFields(newFields);
  };

  const handleDragStart = (index) => {
    setDraggedFieldIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (draggedFieldIndex === null || draggedFieldIndex === dropIndex) return;

    const newFields = [...fields];
    const draggedField = newFields[draggedFieldIndex];
    newFields.splice(draggedFieldIndex, 1);
    newFields.splice(dropIndex, 0, draggedField);

    // Reorder
    newFields.forEach((field, i) => {
      field.order = i;
    });

    setFields(newFields);
    setDraggedFieldIndex(null);
  };

  const handlePreviewTemplate = async () => {
    if (!selectedBond || !user) {
      alert('Please select a bond and ensure you are logged in');
      return;
    }

    if (!user?.id && !user?._id) {
      alert('User ID not found. Please refresh the page and try again.');
      return;
    }

    if (fields.length === 0) {
      alert('Please add at least one field to preview the template');
      return;
    }

    // Validate field names are unique
    const fieldNames = fields.map(f => f.name);
    if (new Set(fieldNames).size !== fieldNames.length) {
      alert('Field names must be unique');
      return;
    }

    try {
      setPreviewingTemplate(true);

      // Prepare template data for preview (without saving)
      const templateData = {
        fields: fields.map((field, index) => ({
          ...field,
          order: index
        })),
        templateConfig,
        pdfTemplateFileName: pdfTemplateFileName
      };

      // Generate field data with default values for preview
      const previewFieldData = {};
      fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          previewFieldData[field.name] = field.defaultValue;
        } else {
          // Set sample values based on field type
          switch (field.type) {
            case 'text':
              previewFieldData[field.name] = 'Sample Text';
              break;
            case 'number':
              previewFieldData[field.name] = 1000;
              break;
            case 'date':
              previewFieldData[field.name] = new Date().toISOString().split('T')[0];
              break;
            case 'email':
              previewFieldData[field.name] = 'sample@example.com';
              break;
            case 'textarea':
              previewFieldData[field.name] = 'Sample text area content';
              break;
            case 'checkbox':
              previewFieldData[field.name] = true;
              break;
            case 'dropdown':
              if (field.options && field.options.length > 0) {
                previewFieldData[field.name] = field.options[0].value;
              }
              break;
            default:
              previewFieldData[field.name] = 'Sample Value';
          }
        }
      });

      // Generate PDF preview without saving template
      const pdfResponse = await locTemplatesAPI.previewPDF(
        templateData,
        previewFieldData,
        selectedBond.id,
        user?.id || user?._id
      );

      // Create blob from response
      const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      
      // Open in new window
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      console.error('Error previewing template:', error);
      alert(error.response?.data?.message || error.message || 'Failed to preview template');
    } finally {
      setPreviewingTemplate(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!selectedBond || !user) {
      alert('Please select a bond and ensure you are logged in');
      return;
    }

    if (!user?.id && !user?._id) {
      alert('User ID not found. Please refresh the page and try again.');
      return;
    }

    if (fields.length === 0) {
      alert('Please add at least one field to the template');
      return;
    }

    // Validate field names are unique
    const fieldNames = fields.map(f => f.name);
    if (new Set(fieldNames).size !== fieldNames.length) {
      alert('Field names must be unique');
      return;
    }

    try {
      setSaving(true);

      const templateData = {
        bondId: selectedBond.id,
        primarySubscriberId: user?.id || user?._id,
        templateName: templateName || `${selectedBond.bondName} - LOC Template`,
        fields: fields.map((field, index) => ({
          ...field,
          order: index
        })),
        templateConfig
      };

      const response = await locTemplatesAPI.createTemplate(templateData);

      if (response.data.success) {
        alert('LOC template created successfully!');
        setShowTemplateBuilder(false);
        setSelectedBond(null);
        fetchMyTemplates(); // Refresh the templates list
      } else {
        throw new Error(response.data.message || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert(error.response?.data?.message || error.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Review': { color: 'yellow', icon: Clock },
      'Ready for Allocation': { color: 'warning', icon: AlertTriangle },
      'Allocation Uploaded': { color: 'success', icon: CheckCircle },
      'Pending L1': { color: 'blue', icon: Clock },
      'Pending L2': { color: 'indigo', icon: Clock },
      'Approved': { color: 'green', icon: CheckCircle }
    };
    const config = statusConfig[status] || { color: 'gray', icon: FileText };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getBondStatus = (bond) => {
    if (bond.status && bond.status !== 'Ready for Allocation') {
      return bond.status;
    }
    return 'Ready for Allocation';
  };

  if (showTemplateBuilder && selectedBond) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-6">
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
                  setShowTemplateBuilder(false);
                  setSelectedBond(null);
                }
              }}
              className="mb-4 text-gray-600 hover:text-gray-900"
            >
              ← Back to Bonds
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Create LOC Template</h1>
            <p className="mt-2 text-sm text-gray-600">
              Bond: <span className="font-medium">{selectedBond.bondName}</span>
            </p>
          </div>

          <div className="p-6 mb-6 bg-white rounded-lg shadow">
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Template Name
              </label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter template name"
              />
            </div>

            <div className="p-4 mb-4 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="mb-1 text-sm font-medium text-blue-900">PDF Template</h3>
                  <p className="text-xs text-blue-700">
                    Using template: <span className="font-mono">{pdfTemplateFileName}</span>
                  </p>
                  <p className="mt-1 text-xs text-blue-600">
                    View the PDF template to see available form fields, then map your input fields to them below.
                  </p>
                </div>
                <button
                  onClick={handleViewPDFTemplate}
                  className="btn-secondary btn-sm"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View PDF Template
                </button>
              </div>
              {pdfFormFields.length > 0 && (
                <div className="pt-3 mt-3 border-t border-blue-200">
                  <p className="mb-2 text-xs text-blue-700">
                    Available PDF Form Fields ({pdfFormFields.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {pdfFormFields.map((pdfField) => (
                      <span
                        key={pdfField.name}
                        className="inline-flex items-center px-2 py-1 font-mono text-xs text-blue-800 bg-blue-100 rounded"
                      >
                        {pdfField.name} ({pdfField.type})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Fields</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfigModal(true)}
                  className="btn-secondary btn-sm"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Template Config
                </button>
                <button
                  onClick={handlePreviewTemplate}
                  disabled={previewingTemplate || fields.length === 0}
                  className="btn-secondary btn-sm"
                >
                  {previewingTemplate ? (
                    <>
                      <Loader className="w-4 h-4 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-1" />
                      Preview PDF
                    </>
                  )}
                </button>
                <button
                  onClick={handleAddField}
                  className="btn-primary btn-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Field
                </button>
              </div>
            </div>

            {fields.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>No fields added yet. Click "Add Field" to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(index)}
                    className="p-4 transition-colors border border-gray-200 rounded-lg hover:border-gray-300"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                        <span className="text-sm font-medium text-gray-700">
                          Field {index + 1}
                        </span>
                      </div>
                      <button
                        onClick={() => handleRemoveField(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Field Name (unique identifier)
                        </label>
                        <input
                          type="text"
                          value={field.name}
                          onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., investor_name"
                        />
                      </div>

                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Field Type
                        </label>
                        <select
                          value={field.type}
                          onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {FIELD_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Label
                        </label>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Investor Name"
                        />
                      </div>

                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          Placeholder
                        </label>
                        <input
                          type="text"
                          value={field.placeholder || ''}
                          onChange={(e) => handleFieldChange(index, 'placeholder', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Optional placeholder text"
                        />
                      </div>

                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">
                          PDF Form Field Name <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={field.pdfFieldName || ''}
                          onChange={(e) => handleFieldChange(index, 'pdfFieldName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select PDF field...</option>
                          {loadingPdfFields ? (
                            <option disabled>Loading PDF fields...</option>
                          ) : pdfFormFields.length === 0 ? (
                            <option disabled>No PDF fields available</option>
                          ) : (
                            pdfFormFields.map((pdfField) => (
                              <option key={pdfField.name} value={pdfField.name}>
                                {pdfField.name} ({pdfField.type})
                              </option>
                            ))
                          )}
                        </select>
                        <p className="mt-1 text-xs text-gray-500">
                          Map this input field to a field in the PDF template
                        </p>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={field.required || false}
                          onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Required Field
                        </label>
                      </div>

                      {field.type === 'number' && (
                        <>
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Min Value
                            </label>
                            <input
                              type="number"
                              value={field.validation?.min || ''}
                              onChange={(e) => handleFieldValidationChange(index, 'min', e.target.value ? Number(e.target.value) : undefined)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                              Max Value
                            </label>
                            <input
                              type="number"
                              value={field.validation?.max || ''}
                              onChange={(e) => handleFieldValidationChange(index, 'max', e.target.value ? Number(e.target.value) : undefined)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </>
                      )}


                      {field.type === 'dropdown' && (
                        <div className="col-span-2">
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            Options
                          </label>
                          <div className="space-y-2">
                            {(field.options || []).map((option, optIndex) => (
                              <div key={optIndex} className="flex gap-2">
                                <input
                                  type="text"
                                  value={option.label}
                                  onChange={(e) => handleDropdownOptionChange(index, optIndex, 'label', e.target.value)}
                                  placeholder="Label"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <input
                                  type="text"
                                  value={option.value}
                                  onChange={(e) => handleDropdownOptionChange(index, optIndex, 'value', e.target.value)}
                                  placeholder="Value"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  onClick={() => handleRemoveDropdownOption(index, optIndex)}
                                  className="px-2 text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => handleAddDropdownOption(index)}
                              className="text-sm text-blue-600 hover:text-blue-800"
                            >
                              + Add Option
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
                    setShowTemplateBuilder(false);
                    setSelectedBond(null);
                    if (previewPdfUrl) {
                      window.URL.revokeObjectURL(previewPdfUrl);
                      setPreviewPdfUrl(null);
                    }
                  }
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handlePreviewTemplate}
                disabled={previewingTemplate || fields.length === 0}
                className="btn-secondary"
              >
                {previewingTemplate ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    Preview PDF
                  </>
                )}
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={saving || fields.length === 0}
                className="btn-primary"
              >
                {saving ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* PDF Template Preview Modal */}
        {showPdfPreview && pdfPreviewUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden shadow-xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">PDF Template Preview</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Review the PDF template to identify form field names for mapping
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPdfPreview(false);
                    if (pdfPreviewUrl) {
                      window.URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
                <iframe
                  src={pdfPreviewUrl}
                  className="w-full h-full min-h-[600px] border border-gray-200 rounded"
                  title="PDF Template Preview"
                />
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPdfPreview(false);
                    if (pdfPreviewUrl) {
                      window.URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Template Config Modal */}
        {showConfigModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Template Configuration</h2>
                  <button
                    onClick={() => setShowConfigModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      PDF Title
                    </label>
                    <input
                      type="text"
                      value={templateConfig.title}
                      onChange={(e) => setTemplateConfig({ ...templateConfig, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Header Text (optional)
                    </label>
                    <input
                      type="text"
                      value={templateConfig.headerText || ''}
                      onChange={(e) => setTemplateConfig({ ...templateConfig, headerText: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Footer Text (optional)
                    </label>
                    <input
                      type="text"
                      value={templateConfig.footerText || ''}
                      onChange={(e) => setTemplateConfig({ ...templateConfig, footerText: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Page Size
                      </label>
                      <select
                        value={templateConfig.pageSize}
                        onChange={(e) => setTemplateConfig({ ...templateConfig, pageSize: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="A4">A4</option>
                        <option value="Letter">Letter</option>
                      </select>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium text-gray-700">
                        Orientation
                      </label>
                      <select
                        value={templateConfig.orientation}
                        onChange={(e) => setTemplateConfig({ ...templateConfig, orientation: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="portrait">Portrait</option>
                        <option value="landscape">Landscape</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-700">
                      Primary Color (hex)
                    </label>
                    <input
                      type="color"
                      value={templateConfig.primaryColor}
                      onChange={(e) => setTemplateConfig({ ...templateConfig, primaryColor: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowConfigModal(false)}
                    className="btn-primary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create LOC</h1>
              <p className="mt-2 text-sm text-gray-600">
                Create your Letter of Commitment (LOC) templates for bonds
              </p>
            </div>
            <button
              onClick={() => setShowBondSelectionModal(true)}
              
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Bond LOC
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader className="w-5 h-5 mr-2 animate-spin" />
            Loading bonds...
          </div>
        ) : error ? (
          <div className="py-8 text-sm text-center text-red-600">{error}</div>
        ) : myTemplates.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-lg shadow">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">No LOC Templates Created</h3>
            <p className="max-w-md mx-auto mb-6 text-gray-600">
              Click "Create Bond LOC" to create your first LOC template for a bond with an active subscription period.
            </p>
            <button
              onClick={() => setShowBondSelectionModal(true)}
              
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Bond LOC
            </button>
          </div>
        ) : (
          <div className="overflow-hidden bg-white shadow sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg font-medium leading-6 text-gray-900">My LOC Templates</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Bond Name</th>
                    <th className="table-header-cell">Issuer</th>
                    <th className="table-header-cell">Subscription Period</th>
                    <th className="table-header-cell">Template Name</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {myTemplates.map((template) => {
                    const bond = template.bondId;
                    if (!bond) return null;
                    
                    return (
                      <tr key={template._id || template.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{bond.bondName || 'Unknown Bond'}</div>
                         
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {typeof bond.issuer === 'object' && bond.issuer?.email
                              ? bond.issuer.email
                              : typeof bond.issuer === 'string'
                              ? bond.issuer
                              : 'Unknown Issuer'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {bond.subscriptionStartDate && bond.subscriptionEndDate ? (
                            <div>
                              <div className="text-sm text-gray-900">
                                {formatDate(bond.subscriptionStartDate)}
                              </div>
                              <div className="text-xs text-gray-500">
                                to {formatDate(bond.subscriptionEndDate)}
                              </div>
                            </div>
                          ) : bond.subscriptionStart && bond.subscriptionEnd ? (
                            <div>
                              <div className="text-sm text-gray-900">
                                {formatDate(bond.subscriptionStart)}
                              </div>
                              <div className="text-xs text-gray-500">
                                to {formatDate(bond.subscriptionEnd)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">—</div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{template.templateName || 'LOC Template'}</div>
                          <div className="text-xs text-gray-500">
                            {template.fields?.length || 0} field{template.fields?.length !== 1 ? 's' : ''}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            template.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {template.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            className="btn-secondary btn-sm"
                            onClick={() => handlePreviewExistingTemplate(template)}
                            disabled={previewingTemplateId === (template._id || template.id)}
                          >
                            {previewingTemplateId === (template._id || template.id) ? (
                              <>
                                <Loader className="w-3 h-3 mr-1 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Preview
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bond Selection Modal */}
        {showBondSelectionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Select Bond to Create LOC</h2>
                  <button
                    onClick={() => setShowBondSelectionModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {loadingAvailableBonds ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Loading available bonds...
                  </div>
                ) : availableBonds.length === 0 ? (
                  <div className="py-8 text-center">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="mb-2 text-lg font-medium text-gray-900">No Available Bonds</h3>
                    <p className="mb-6 text-gray-600">
                      All bonds with subscription periods already have LOC templates, or no bonds are available.
                    </p>
                    <button
                      onClick={() => setShowBondSelectionModal(false)}
                      className="btn-secondary"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Bond Details</th>
                          <th className="table-header-cell">Issuer</th>
                          <th className="table-header-cell">Amount & Rate</th>
                          <th className="table-header-cell">Subscription Period</th>
                          <th className="table-header-cell">Status</th>
                          <th className="table-header-cell" style={{ minWidth: '120px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {availableBonds.map((bond) => (
                          <tr key={bond.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{bond.bondName}</div>
                                <div className="text-sm text-gray-500">{bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{bond.issuer}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{formatCurrency(bond.nominalAmount)}</div>
                              <div className="text-sm text-gray-500">
                                {bond.profitRate ? `${bond.profitRate}% p.a.` : `${bond.couponRate}% p.a.`}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {bond.subscriptionStartDate && bond.subscriptionEndDate ? (
                                <div>
                                  <div className="text-sm text-gray-900">
                                    {formatDate(bond.subscriptionStartDate)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    to {formatDate(bond.subscriptionEndDate)}
                                  </div>
                                  <div className="mt-2">
                                    {(() => {
                                      const now = new Date();
                                      const endDate = new Date(bond.subscriptionEndDate);
                                      const startDate = new Date(bond.subscriptionStartDate);

                                      if (now < startDate) {
                                        return (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Upcoming
                                          </span>
                                        );
                                      } else if (now >= startDate && now <= endDate) {
                                        return (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            Active
                                          </span>
                                        );
                                      } else {
                                        return (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            Completed
                                          </span>
                                        );
                                      }
                                    })()}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">
                                  No subscription period
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {getStatusBadge(getBondStatus(bond))}
                            </td>
                            <td className="px-6 py-4" style={{ whiteSpace: 'nowrap' }}>
                              <button
                                className="btn-primary"
                                onClick={() => handleCreateLOC(bond)}
                                
                                style={{ 
                                  whiteSpace: 'nowrap',
                                  fontSize: '11px',
                                  padding: '4px 8px',
                                  lineHeight: '1.2'
                                }}
                              >
                                Create LOC
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PDF Template Preview Modal */}
        {showPdfPreview && pdfPreviewUrl && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden shadow-xl">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">PDF Template Preview</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Review the PDF template to identify form field names for mapping
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowPdfPreview(false);
                    if (pdfPreviewUrl) {
                      window.URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
                <iframe
                  src={pdfPreviewUrl}
                  className="w-full h-full min-h-[600px] border border-gray-200 rounded"
                  title="PDF Template Preview"
                />
              </div>
              <div className="flex justify-end px-6 py-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPdfPreview(false);
                    if (pdfPreviewUrl) {
                      window.URL.revokeObjectURL(pdfPreviewUrl);
                      setPdfPreviewUrl(null);
                    }
                  }}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateLOC;
